import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import type { Product } from './model';

/** A product's bills of materials, per variant; editing opens the bill of materials form. */
export function BomPanel({ product: p }: { product: Product }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canUpdate = can('products.update');
  return (
    <Stack spacing={2.5}>
      <Alert severity="info" variant="outlined" icon={<AccountTreeRounded />}>
        {!p.variants.some((v) => v.recipe)
          ? `No bill of materials yet. Add one to make ${p.name} from components.`
          : p.trackInventory
            ? `${p.name} tracks its own stock: each sale first makes what is not in stock from its components.`
            : `${p.name} has no stock of its own: each sale takes its components from stock.`}
      </Alert>
      {p.variants.map((v) => (
        <Card key={v.id}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontSize: '1.0625rem' }}>
                  {p.variants.length > 1 ? v.displayName : 'Bill of materials'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Per 1 {p.baseUnit.symbol}
                </Typography>
              </Box>
              {v.recipe && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={v.recipe.returnsToStock ? 'primary' : 'default'}
                  label={
                    v.recipe.returnsToStock ? 'Return to inventory: on' : 'Return to inventory: off'
                  }
                />
              )}
              {canUpdate && (
                <Button
                  variant="outlined"
                  startIcon={v.recipe ? <EditRounded /> : <AddRounded />}
                  onClick={() => void navigate(`/bom/${p.id}/${v.id}`)}
                >
                  {v.recipe ? 'Edit' : 'Add bill of materials'}
                </Button>
              )}
            </Stack>
            {v.recipe ? (
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {v.recipe.lines.map((l) => (
                    <Box
                      component="tr"
                      key={l.variantId}
                      sx={{ '& td': { py: 1, borderTop: 1, borderColor: 'divider' } }}
                    >
                      <td>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {l.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {l.sku}
                        </Typography>
                      </td>
                      <Box component="td" className="tabular" sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          {l.qty} {l.unitSymbol}
                        </Typography>
                        {l.unitSymbol !== l.baseUnitSymbol && (
                          <Typography variant="caption" color="text.secondary">
                            = {l.qtyBase} {l.baseUnitSymbol}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </tbody>
              </Box>
            ) : (
              <Typography color="text.secondary">No bill of materials.</Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
